/**
 * VERTEX ANIMATION TEXTURE bake.
 *
 * The crowd cannot afford 500 THREE.SkinnedMesh objects, so every clip is
 * pre-evaluated at boot into a float texture of BONE SKINNING MATRICES. One
 * InstancedMesh then draws hundreds of warriors with a single draw call: the
 * vertex shader reads four matrices per vertex out of the texture using the
 * ordinary skinIndex/skinWeight attributes the character meshes already carry.
 *
 * LAYOUT
 *   Each frame occupies BONE_COUNT * 3 consecutive texels (a 3x4 row-major
 *   matrix per bone, one texel per row). Frames are packed FPR-per-row so the
 *   texture stays well under any max-texture-size limit:
 *
 *     col = (frame % FPR) * BONE_COUNT * 3 + bone * 3 + row(0..2)
 *     row = floor(frame / FPR)
 *
 *   Frame ranges are addressed per (kind, clip) through `base` / `count`.
 *   Frame `base + count - 1` is the wrap frame of a looping clip (identical to
 *   `base`), so a shader can map phase 0..1 straight onto the range.
 *
 * The bake is per KIND because the rigs differ in proportion (a general is
 * 1.94 m and broader than a 1.75 m grunt), and a skinning matrix bakes the
 * bind pose in.
 */
import * as THREE from 'three';
import { BONE_COUNT } from '../chars/rig.js';
import { makeQ, makeP, makeM, eulerToQuat, poseToMatrices } from './pose.js';
import { sampleClip, wristCompensate } from './clips.js';

const TEXELS_PER_FRAME = BONE_COUNT * 3;

/**
 * @param {Array<{name:string, rig:object}>} kinds
 * @param {Array} clips  compiled clips from clips.js
 * @param {{fps?:number, framesPerRow?:number}} o
 */
export function bakeVAT(kinds, clips, o = {}) {
  const fps = o.fps ?? 30;
  const FPR = o.framesPerRow ?? 16;
  const nk = kinds.length, nc = clips.length;

  // ---- frame budget ------------------------------------------------------
  // A clip may ask for a denser bake than the default. The crowd shader snaps
  // to the NEAREST baked frame, so a locomotion cycle sampled at 30 Hz makes a
  // planted foot jump ~25 cm per frame while the body glides — the animation
  // is right and the crowd still skates. Locomotion is therefore baked at 90 Hz
  // (see `fps` in clips.js), which costs a few hundred KB and drops that step
  // to under 10 cm.
  const perClip = new Int32Array(nc);
  let framesPerKind = 0;
  for (let c = 0; c < nc; c++) {
    const n = Math.max(2, Math.min(220, Math.round(clips[c].dur * (clips[c].fps || fps)) + 1));
    perClip[c] = n; framesPerKind += n;
  }
  const total = framesPerKind * nk;
  const width = FPR * TEXELS_PER_FRAME;
  const height = Math.ceil(total / FPR);
  const data = new Float32Array(width * height * 4);

  const base = new Int32Array(nk * nc);
  const count = new Int32Array(nk * nc);

  // ---- scratch (module-local, reused for the whole bake) -----------------
  const eul = new Float32Array(BONE_COUNT * 3);
  const pos = new Float32Array(BONE_COUNT * 3);
  const q = makeQ();
  const world = makeM();
  const skin = makeM();

  let f = 0;
  const t0 = (typeof performance !== 'undefined' ? performance.now() : 0);
  for (let k = 0; k < nk; k++) {
    const rig = kinds[k].rig;
    for (let c = 0; c < nc; c++) {
      const clip = clips[c], n = perClip[c];
      base[k * nc + c] = f; count[k * nc + c] = n;
      for (let i = 0; i < n; i++) {
        const tn = n > 1 ? i / (n - 1) : 0;
        sampleClip(clip, tn, eul, pos);
        eulerToQuat(eul, q);
        wristCompensate(q);
        poseToMatrices(q, pos, rig, world, skin);
        const row = (f / FPR) | 0;
        const col = (f - row * FPR) * TEXELS_PER_FRAME;
        let d = (row * width + col) * 4;
        for (let b = 0; b < BONE_COUNT; b++) {
          const m = b * 12;
          data[d] = skin[m]; data[d + 1] = skin[m + 1]; data[d + 2] = skin[m + 2]; data[d + 3] = skin[m + 3];
          data[d + 4] = skin[m + 4]; data[d + 5] = skin[m + 5]; data[d + 6] = skin[m + 6]; data[d + 7] = skin[m + 7];
          data[d + 8] = skin[m + 8]; data[d + 9] = skin[m + 9]; data[d + 10] = skin[m + 10]; data[d + 11] = skin[m + 11];
          d += 12;
        }
        f++;
      }
    }
  }

  const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat, THREE.FloatType);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.generateMipmaps = false;
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;
  texture.name = 'animVAT';

  const kindIndex = {};
  kinds.forEach((k, i) => { kindIndex[k.name] = i; });

  return {
    texture, width, height, framesPerRow: FPR, texelsPerFrame: TEXELS_PER_FRAME,
    fps, kinds: kinds.map(k => k.name), kindIndex, clipCount: nc,
    base, count, totalFrames: total,
    megabytes: +(data.byteLength / 1048576).toFixed(2),
    bakeMs: +((typeof performance !== 'undefined' ? performance.now() : 0) - t0).toFixed(1),
    /** frame range for (kindIdx, clipIdx) */
    range(k, c) { return { base: base[k * nc + c], count: count[k * nc + c] }; },
    dispose() { texture.dispose(); },
  };
}
